<template>
  <CoreDrawer
    :value="value"
    :title="title"
    :is-back="isBack"
    :attach="attach"
    :title-truncate="titleTruncate"
    @input="$emit('input', false)"
    @back="$emit('back')"
  >
    <template #header>
      <CoreSearchField
        v-if="isVisibleSearch"
        v-model.trim="searchQuery"
        autofocus
        maxlength="150"
        class="mt-2"
        :placeholder="placeholder || title"
        @click:clear="handleClickClear"
        @input="$emit('search:change', $event)"
      />
    </template>
    <template #append-header>
      <slot name="append-header" />
    </template>
    <template #default>
      <DrawerError
        v-if="error"
        class="py-9"
      />
      <DrawerSkeleton
        v-else-if="loading"
        class="px-2"
      />
      <CoreSelectionList
        v-else
        ref="selectionListRef"
        class="py-2"
        :items="items"
        :value="selectedItem || defaultItem"
        :search-input="noFilter ? undefined : searchQuery"
        :end-icon="endIcon"
        @input="$emit('click-item', $event)"
      >
        <template #empty>
          <slot name="empty-list">
            <DrawerNotFound class="py-3" />
          </slot>
        </template>
      </CoreSelectionList>
    </template>
    <template
      v-if="hasFooter"
      #footer
    >
      <slot name="footer" />
    </template>
  </CoreDrawer>
</template>

<script setup lang="ts">
/**
 * Drawer для выбора пункта из списка (город, специальность, фильтр и т.п.).
 *
 * Обёртка над `CoreDrawer` с поиском (`CoreSearchField`), списком
 * (`CoreSelectionList`) и состояниями loading / error / empty.
 * При открытии сбрасывает поиск и прокручивает к текущему выбору.
 *
 * Не подходит, если нужна только оболочка без списка — тогда `CoreDrawer`.
 *
 * @example
 * <CoreSelectionDrawer
 *   has-search
 *   title="Город"
 *   :value="isOpen"
 *   :items="cities"
 *   :selected-item="city"
 *   \@input="isOpen = false"
 *   \@click-item="onSelect"
 * />
 *
 * Слоты: `append-header`, `empty-list`, `footer`.
 * Ref: `resetSearch()`, `scrollToActiveItem()`.
 */
import {
  ref, computed, toRefs, watch, nextTick,
} from 'vue'
import CoreDrawer from 'src/core/CoreDrawer/CoreDrawer.vue'
import CoreSearchField from 'src/core/CoreSearchField/CoreSearchField.vue'
import CoreSelectionList from 'src/core/CoreSelectionList/CoreSelectionList.vue'
import DrawerError from 'src/core/CoreSelectionDrawer/components/DrawerError.vue'
import DrawerSkeleton from 'src/core/CoreSelectionDrawer/components/DrawerSkeleton.vue'
import DrawerNotFound from 'src/core/CoreSelectionDrawer/components/DrawerNotFound.vue'
import type { SelectionDrawerItem } from 'src/core/CoreSelectionDrawer/types'
import type { CoreSelectionListEndIcon } from 'src/core/CoreSelectionList/types'

const emit = defineEmits(['input', 'back', 'click-item', 'search:change'])

const props = withDefaults(defineProps<{
  value: boolean
  title: string
  placeholder?: string
  items: SelectionDrawerItem[]
  selectedItem?: SelectionDrawerItem
  titleTruncate?: boolean
  iconClose?: string
  loading?: boolean
  error?: boolean
  isBack?: boolean
  hasSearch?: boolean
  hasFooter?: boolean
  isCheckedVariant?: boolean
  noFilter?: boolean
  noLoadingSearch?: boolean
  attach?: string
}>(), {
  placeholder: undefined,
  selectedItem: undefined,
  attach: '',
  iconClose: 'ui-icon-close-not-a-circle',
})
const {
  loading, error, value, hasSearch, noLoadingSearch, items, selectedItem,
} = toRefs(props)

const selectionListRef = ref<InstanceType<typeof CoreSelectionList>>()
const searchQuery = ref('')
const isVisibleSearch = computed(() => {
  if (noLoadingSearch?.value) {
    return !error?.value && hasSearch?.value
  }

  return !loading?.value && !error?.value && hasSearch?.value
})
const endIcon = computed<CoreSelectionListEndIcon>(() => (
  props.isCheckedVariant ? 'check' : 'arrow'
))
const defaultItem = computed(() => (
  items.value.find(item => item.translit === 'default-item')
))

watch(value, async isOpen => {
  resetSearch()

  if (isOpen) {
    await nextTick()
    scrollToActiveItem()
  }
})

function scrollToActiveItem() {
  selectionListRef.value?.scrollToActiveItem()
}

function resetSearch() {
  searchQuery.value = ''
  emit('search:change', '')
}

function handleClickClear() {
  resetSearch()
  scrollToActiveItem()
}

defineExpose({
  resetSearch,
  scrollToActiveItem,
})
</script>
